import React, { useState, useEffect } from 'react';
import { api, apiClient } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calculator, Info, Lock } from 'lucide-react';
import PropTypes from 'prop-types';

// Calls the same backend calculation engine (calculateOptimalSection125 in
// proposalCalculationService.ts) as the admin Proposal tool and the ad hoc
// employee calculator, via /proposals/calculate-single — instead of a
// separate client-side tax-bracket implementation that could silently
// disagree with what's shown elsewhere in the app.
async function calculateOptimalFlex(income, status, deps, employee) {
  const response = await apiClient.post('/proposals/calculate-single', {
    annual_salary: Number(income || 0),
    filing_status: status,
    state: employee?.state_of_residence || employee?.state || undefined,
    w4_version: employee?.w4_version || 'pre_2020',
    w4_allowances: Number(employee?.w4_allowances || 0),
    w4_step3_total: Number(employee?.w4_step3_total || 0),
    w4_step2_multiple_jobs: Boolean(employee?.w4_step2_multiple_jobs),
    pre_tax_monthly: Number(employee?.pre_tax_monthly || 0),
    dependents: Number(deps || 0),
    employer_id: employee?.employer_id || undefined,
  });

  const { eligible, computed_data, all_results } = response.data || {};
  if (!eligible || !computed_data) {
    return { optimalFlexAmount: 0, monthlyBenefit: 0, annualBenefit: 0, taxSavings: 0, ficaSavings: 0, allResults: [] };
  }

  return {
    optimalFlexAmount: Number(computed_data.section125_monthly ?? computed_data.section125 ?? 0),
    monthlyBenefit: Number(computed_data.net_flex || 0),
    annualBenefit: Number(computed_data.net_flex || 0) * 12,
    taxSavings: Number(computed_data.total_tax_savings || 0),
    ficaSavings: Number(computed_data.fica_tax_savings || 0),
    allResults: all_results || [],
  };
}

function normalizeFilingStatus(status) {
  if (!status) return 'single';
  const s = String(status).toLowerCase();
  if (s === 'married') return 'married';
  if (s.startsWith('married') || s.includes('mfj') || s.includes('joint')) return 'married';
  return 'single';
}

export default function FlexCalculator({ employee, onOptimalCalculated, onAutoSaved }) {
  const [annualIncome, setAnnualIncome] = useState(50000);
  const [filingStatus, setFilingStatus] = useState('single');
  const [dependents, setDependents] = useState(0);
  const [adminFee, setAdminFee] = useState(25);
  const [result, setResult] = useState(null);
  const [autoCalculated, setAutoCalculated] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (employee) {
        // Auto-populate from employee demographics
        const salary = employee.annual_salary && employee.annual_salary > 0 ? Number(employee.annual_salary) : 50000;
        const status = normalizeFilingStatus(employee.filing_status);
        const fee = employee.admin_fee ? Number(employee.admin_fee) : 25;

        // Try to get dependents from employee record, then count family dependents
        let deps = employee.dependents || 0;

        try {
          const familyMembers = await api.entities.FamilyMember.filter({
            employee_id: employee.id,
            is_active: true
          });

          const dependentCount = familyMembers.filter(m =>
            m.is_dependent &&
            m.relationship &&
            m.relationship.toLowerCase() !== 'spouse'
          ).length;

          deps = Math.max(deps, dependentCount);
        } catch (err) {
          console.error('Could not load family members for dependent count:', err);
        }

        setAnnualIncome(salary);
        setFilingStatus(status);
        setDependents(deps);
        setAdminFee(fee);

        // Auto-calculate and save on page load (once per mount)
        if (!autoCalculated && salary > 0) {
          await handleCalculate({ income: salary, status, deps });
          setAutoCalculated(true);
        }
      }
    };

    loadData();
  }, [employee]);

  const handleCalculate = async (overrides) => {
    const income = overrides?.income ?? annualIncome;
    const status = normalizeFilingStatus(overrides?.status ?? filingStatus);
    const deps = overrides?.deps ?? dependents;

    const calculation = await calculateOptimalFlex(income, status, deps, employee);
    setResult(calculation);

    // Auto-save the optimal flex amount to employee record
    if (employee && calculation) {
      try {
        await api.entities.Employee.update(employee.id, {
          flex_allocation: calculation.optimalFlexAmount,
          optimal_flex_amount: calculation.optimalFlexAmount,
          calculated_tax_savings: calculation.annualBenefit // Net annual benefit
        });
        if (onAutoSaved) {
          await onAutoSaved();
        }
      } catch (err) {
        console.error('Failed to auto-save flex calculation:', err);
      }
    }
    
    if (onOptimalCalculated) {
      onOptimalCalculated(calculation);
    }
  };

  const chartData = result?.allResults?.filter((_, idx) => idx % 4 === 0) || [];

  // Original payroll/demographic numbers are locked once loaded for a real
  // employee so they can't be edited into a different "what-if" scenario here —
  // corrections have to go through HR (the source of record).
  const inputsLocked = Boolean(employee);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-500" />
            Section 125 Premium Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {inputsLocked && (
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
              <span>
                These values are locked to your original payroll and demographic numbers on file. Contact HR if any of them need to be corrected.
              </span>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="flex items-center gap-1">
                Annual Income
                {inputsLocked && <Lock className="w-3 h-3 text-slate-400" />}
              </Label>
              <Input
                type="number"
                value={annualIncome}
                onChange={(e) => setAnnualIncome(Number(e.target.value))}
                placeholder="50000"
                disabled={inputsLocked}
                className={inputsLocked ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}
              />
              {employee?.annual_salary > 0 && (
                <p className="text-xs text-emerald-600 mt-1">
                  ✓ Loaded from your demographics
                </p>
              )}
            </div>

            <div>
              <Label className="flex items-center gap-1">
                Filing Status
                {inputsLocked && <Lock className="w-3 h-3 text-slate-400" />}
              </Label>
              <Select value={filingStatus} onValueChange={setFilingStatus} disabled={inputsLocked}>
                <SelectTrigger className={inputsLocked ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                </SelectContent>
              </Select>
              {employee?.filing_status && (
                <p className="text-xs text-emerald-600 mt-1">
                  ✓ Loaded from your demographics
                </p>
              )}
            </div>

            <div>
              <Label className="flex items-center gap-1">
                Number of Dependents
                {inputsLocked && <Lock className="w-3 h-3 text-slate-400" />}
              </Label>
              <Input
                type="number"
                value={dependents}
                onChange={(e) => setDependents(Number(e.target.value))}
                min="0"
                disabled={inputsLocked}
                className={inputsLocked ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}
              />
              {employee?.dependents > 0 && (
                <p className="text-xs text-emerald-600 mt-1">
                  ✓ Loaded from your demographics
                </p>
              )}
            </div>


          </div>

          <Button 
            onClick={handleCalculate}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          >
            Calculate Optimal Premium Amount
          </Button>
        </CardContent>
      </Card>

      {result && (
        <>
          {result.optimalFlexAmount === 0 ? (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium mb-2">No Optimal Amount Calculated</p>
                    <p>
                      Unable to calculate an optimal premium amount. This may happen when:
                    </p>
                    <ul className="mt-2 space-y-1">
                      <li>• Income data is missing or invalid</li>
                      <li>• Income is below minimum thresholds</li>
                      <li>• Tax brackets cannot be determined</li>
                    </ul>
                    <p className="mt-2 font-medium">
                      Please verify your salary and filing status are correct.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
          <>
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <p className="text-sm text-emerald-600 mb-1">Optimal Premium Amount</p>
                <p className="text-2xl font-bold text-emerald-900">
                  ${result.optimalFlexAmount.toFixed(2)}
                  <span className="text-sm font-normal">/month</span>
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600 mb-1">Monthly Benefit</p>
                <p className="text-2xl font-bold text-blue-900">
                  ${result.monthlyBenefit.toFixed(2)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <p className="text-sm text-purple-600 mb-1">Tax Savings</p>
                <p className="text-2xl font-bold text-purple-900">
                  ${result.taxSavings.toFixed(2)}
                  <span className="text-sm font-normal">/month</span>
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600 mb-1">Annual Benefit</p>
                <p className="text-2xl font-bold text-amber-900">
                  ${result.annualBenefit.toFixed(2)}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Savings Curve</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="flexAmount"
                    label={{ value: 'Premium Amount ($)', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    label={{ value: 'Monthly Benefit ($)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => `$${value.toFixed(2)}`}
                    labelFormatter={(label) => `Premium: $${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="totalBenefit" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Total Benefit"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="taxSavings" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Tax Savings"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-2">Understanding Your Results</p>
                  <ul className="space-y-1 text-blue-800">
                    <li>• <strong>Optimal Premium Amount:</strong> The monthly pre-tax contribution that maximizes your benefit</li>
                    <li>• <strong>Tax Savings:</strong> Combined federal income tax + FICA savings (7.65%)</li>
                    <li>• <strong>Monthly Benefit:</strong> Your net gain after admin fees</li>
                    <li>• <strong>86% Rule:</strong> Unused premium dollars can be reimbursed at 86% for qualified medical expenses</li>
                    <li>• <strong>Employer Benefit:</strong> Your employer also saves on FICA contributions</li>
                  </ul>
                  <p className="mt-3 font-medium">
                    💡 Tip: Adjust values above and recalculate to see how changes affect your savings!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          </>
          )}
        </>
      )}
    </div>
  );
}

FlexCalculator.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string,
    annual_salary: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    filing_status: PropTypes.string,
    dependents: PropTypes.number,
    admin_fee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    flex_allocation: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    optimal_flex_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  onOptimalCalculated: PropTypes.func,
  onAutoSaved: PropTypes.func
};